const oneDay = 24 * 60 * 60 * 1000;
const oneSecond = 1000;

/*
  Adds x days on a given date and returns the resulted Date.
  @param {Date} date - Given date
  @param {Number} days - Number of days to add.
*/
export function addDays(date, days) {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + days);
  return newDate;
}

/*
  Remove x days on a given date and returns the resulted Date.
  @param {Date} date - Given date
  @param {Number} days - Number of days to remove.
*/
export function removeDays(date, days) {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() - days);
  return newDate;
}

/*
  Return the start date formatted as string , receives the bidSessionStartDateUTCStr
  and prevBuffer
*/

export function getCalendarStartDate(bidSessionStartDateUTCStr, prevBuffer) {
  let calendarStartDateTs = removeDays(bidSessionStartDateUTCStr, prevBuffer).getTime();
  calendarStartDateTs = new Date(calendarStartDateTs).toISOString();
  return calendarStartDateTs;
}

/*
  Add the after buffers to the endDate, and returns the endDate
  formatted with 23:59:59.
  Receives the params bidSessionEndDateUTCStr and afterBuffer
*/
export function getCalendarEndDate(bidSessionEndDateUTCStr, afterBuffer) {
  let calendarEndDateTs = addDays(bidSessionEndDateUTCStr, afterBuffer).getTime();
  calendarEndDateTs -= oneSecond;
  calendarEndDateTs = new Date(calendarEndDateTs).toISOString();
  return calendarEndDateTs;
}

/*
  Returns the difference in days between two given dates.
  @param {Date} date1 - Start reference
  @param {Date} date2 - End reference
*/
export function getTimeDiff(date1, date2) {
  let timeDiff = Math.abs(date2.getTime() - date1.getTime());
  timeDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
  return timeDiff + 1;
}

export function getDaysBetweenDates(start, end) {
  const diff = (end - start) / oneDay;
  return Math.ceil(Math.abs(diff));
}

/*
  Returns the difference in hours between two given dates.
  @param {Date} date1 - Start reference
  @param {Date} date2 - End reference
*/
export function getHourDiff(date1, date2) {
  return Math.abs(date1 - date2) / 36e5;
}

// return the difference between 2 timestamps in 00H00 format
export function getTimeDifferenceFromTimestamp(timestamp1, timestamp2) {
  // Get the difference in milliseconds
  const diffInMs = Math.abs(timestamp1 - timestamp2); // Use absolute value to avoid negative times

  // Convert milliseconds to total minutes
  const totalMinutes = Math.floor(diffInMs / 1000 / 60);

  // Calculate hours and minutes
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  // Format hours and minutes to be two digits
  const formattedHours = String(hours).padStart(2, '0');
  const formattedMinutes = String(minutes).padStart(2, '0');

  return `${formattedHours}H${formattedMinutes}`;
}

/*
  Returns the factor to be multiplied in pixels from a given hour.
  For example, calculates difference inside a day on the Linear Calendar table.
  @param {Number} hours - Hours to calculate a factor
*/
export function convertHoursToPixels(hours) {
  const quotient = Math.floor(hours / 24);
  let reminder = hours % 24;
  if (reminder < 6) {
    reminder = 0;
  } else if (reminder >= 6 && reminder < 12) {
    reminder = 0.25;
  } else if (reminder >= 12 && reminder < 18) {
    reminder = 0.5;
  } else {
    reminder = 0.75;
  }
  return quotient + reminder;
}

/*
  Returns the length in units, considering 1 as 1 day.
  @param {Number} hours - Duration in hours of an activity.
  @param {Number} pivot - Day width in pixels.
*/
export function getActivityLength(hours, pivot) {
  const quotient = Math.floor(hours / 24);
  const reminder = hours % 24;
  return (quotient * pivot) + (reminder * (pivot / 24));
}

/*
  Returns a string in the format AAAA-MM-DD HH:mm.
  @param {Date} date1 - Reference Date
*/
export function formatActivityDate(date) {
  return (
    `${date.getFullYear()}-${(`0${date.getMonth() + 1}`).slice(-2)}-${(`0${date.getDate()}`).slice(-2)}
     ${(`0${date.getHours()}`).slice(-2)}:${(`0${date.getMinutes()}`).slice(-2)}`
  );
}

export function formatActivityDateWithNoTime(date) {
  return (
      `${date.getFullYear()}-${(`0${date.getMonth() + 1}`).slice(-2)}-${(`0${date.getDate()}`).slice(-2)}`
  );
}

/*
  Returns a string in the format AAAA-MM-DD HH:mm - AAAA-MM-DD HH:mm.
  to represent a period of time.
  @param {Timestamp} date1 - Start reference
  @param {Timestamp} date2 - End reference
*/
export function convertActivityDetailsDateInterval(date1, date2) {
  const startDate = new Date(date1);
  const endDate = new Date(date2);
  return `(${formatActivityDate(startDate)} - ${formatActivityDate(endDate)})`;
}

export function getFormattedPairingDate(month, date) {
  const formattedDate = `${(`0${month + 1}`).slice(-2)}/${(`0${date}`).slice(-2)}`;
  return formattedDate;
}

export function getFormattedPairingTime(hours, minutes) {
  return `${(`0${hours}`).slice(-2)}:${(`0${minutes}`).slice(-2)}`;
}

export function getFormattedDate(date) {
  return `${date.getFullYear()}-${(`0${date.getMonth() + 1}`).slice(-2)}-${(`0${date.getDate()}`).slice(-2)} ${getFormattedPairingTime(date.getHours(), date.getMinutes())}`;
}

export function getFormattedDateNoYear(date) {
  return `${(`0${date.getMonth() + 1}`).slice(-2)}-${(`0${date.getDate()}`).slice(-2)} ${getFormattedPairingTime(date.getHours(), date.getMinutes())}`;
}

/*
  Returns a string in the format XhYm to represent an amount of minutes with hours.
  @param {number} credit - Reference minutes
*/
export function formatCredit(credit) {
  const hours = Math.floor(credit / 60);
  const minutes = credit % 60;
  return `${hours}h${(`0${minutes}`).slice(-2)}`;
}

/*
  Returns the number of hours from a given minutes amount.
  @param {Number} credit - minutes to calculate an hour quantity
*/
export function creditToHours(credit) {
  return Math.floor(credit / 60);
}

/*
  Returns the number of minutes as quotient from a given minutes amount.
  @param {Number} credit - Minutes to extract quotient
*/
export function extractMinutes(credit) {
  return credit % 60;
}

export function getUTC(date) {
  if (!date) return null;
  if (typeof date.getMonth !== 'function') {
    date = new Date(date);
  }
  date.setMinutes(
    date.getMinutes() + date.getTimezoneOffset()
  );
  return date;
}

export function arrayDateToUtcString(array, startDayOffsetInMins) {
  const year = array[0];
  const month = `0${array[1]}`.substr(-2);
  const day = `0${array[2]}`.substr(-2);
  const time = formatMinutesToHoursWithMinutesISO(startDayOffsetInMins,2);
  return `${year}-${month}-${day}T${time}`;
}

export function arrayDateToUtcStringWithNoTime(array) {
  const year = array[0];
  const month = `0${array[1]}`.substr(-2);
  const day = `0${array[2]}`.substr(-2);
  return `${year}-${month}-${day}`;
}

export function lpad(str, length, padString) {
  while (str.length < length) {
    str = padString + str;
  }
  return str;
}

export function formatMinutesToHoursWithMinutesISO(minutes, lpadHours = 2) {
  let formatter = (completeHours, remainderMinutes) => {
    return `${completeHours}:${remainderMinutes}:00.000Z`
  };
  return formatMinutesToHoursWithMinutesInternal(minutes, lpadHours, formatter);
}

export function formatMinutesToHoursWithMinutes(minutes, lpadHours = 2) {
  if (typeof minutes === 'string') {
    return minutes;
  }
  let formatter = (completeHours, remainderMinutes) => {
    return `${completeHours}h${remainderMinutes}`
  };
  return formatMinutesToHoursWithMinutesInternal(minutes, lpadHours, formatter);
}

function formatMinutesToHoursWithMinutesInternal(minutes, lpadHours = 2, formatter) {
  if (typeof minutes === 'undefined') {
    return '';
  }
  let completeHours = Math.trunc(minutes / 60).toString();
  completeHours = lpad(completeHours, lpadHours, 0);
  let remainderMinutes = (minutes % 60).toString();
  remainderMinutes = lpad(remainderMinutes, 2, 0);
  return formatter(completeHours, remainderMinutes);
}


export function formatHistoryDates(date) {
  if (!Array.isArray(date)) return false;
  date[1] -= 1;
  return new Date(...date).toString().split(' 00:')[0];
}

export function formatHistoryDatesInFrench(date) {
  const options = {
    weekday: 'short', // Abbreviated weekday name
    year: 'numeric',
    month: 'short', // Abbreviated month name
    day: 'numeric'
  }
  if (!Array.isArray(date)) return false;
  date[1] -= 1;
  return new Date(...date).toLocaleDateString('fr-FR',options).split(' 00:')[0];
}

export function formatDate(date) {
  return new Date(date).toString().split(' 00:')[0];
}

export function formatDateInFrench(date) {
  const options = {
    weekday: 'short', // Abbreviated weekday name
    year: 'numeric',
    month: 'short', // Abbreviated month name
    day: 'numeric'
  }
  return new Date(date).toLocaleDateString('fr-FR',options).split(' 00:')[0];
}

export function getTimeFromDateTimestamp(timestamp) {
  const date = new Date(timestamp);

  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
}

export function getFormattedDateWithMonthName(month, date, year) {
  const monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];
  const formattedDate = `${monthNames[month]} ${(`0${date}`).slice(-2)}, ${year}`;
  return formattedDate;
}

export function convertHoursAndMinutesToMinutes(hours, minutes) {
  const totalTimeInMinutes = (hours * 60) + minutes;
  return totalTimeInMinutes;
}

export function calculateNumberOfDaysFromMinutes(minutes) {
  const days = Math.floor(minutes / (60 * 24));
  return days;
}


export function getFormattedPairingStartDateWithMonthNameAndYear(relativeStartFromStation) {
  const departure = getUTC(relativeStartFromStation);
  const pairingStartDate = getFormattedDateWithMonthName(departure.getMonth(), departure.getDate(), departure.getFullYear());
  return pairingStartDate;
}

export function getFormattedPairingEndDateWithMonthNameAndYear(relativeStartFromStation, pairingDurationInMin) {
  const departure = getUTC(relativeStartFromStation);
  const pairingStartTimeInMinutes = convertHoursAndMinutesToMinutes(departure.getHours(), departure.getMinutes());
  const numberOfDays = calculateNumberOfDaysFromMinutes(pairingStartTimeInMinutes + pairingDurationInMin);
  if (numberOfDays > 0) {
    const pairingEndDate = getFormattedDateWithMonthName(
      departure.getMonth(),
      departure.getDate() + numberOfDays,
      departure.getFullYear()
    );
    return `to ${pairingEndDate}`;
  }
  return null;
}
